import type { Level, Room, Vec2, Wall } from '../hp/types';
import { contains, DEFAULT_WALL_THICKNESS, polygonArea } from './edit';

/**
 * A room's floor as the app measures it (`Room.GetSimilarShape(true)` → `Room.GetArea`): the outline of its wall axes
 * moved inward by half of each wall's thickness. A room keeps its outline on the axes (`rebuildRooms`), so the area
 * the plan and the inspector show, and the width and length typed for it, are read from here — the numbers the app
 * shows, not the axis outline's, which counts half of every wall.
 */

/** The wall a room's edge runs along: collinear with it and covering its middle. */
function wallOnEdge(walls: readonly Wall[], a: Vec2, b: Vec2): Wall | undefined {
  const middle = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
  let best: { wall: Wall; distance: number } | undefined;
  for (const wall of walls) {
    const dx = wall.end.x - wall.start.x, dz = wall.end.z - wall.start.z, length = Math.hypot(dx, dz);
    if (length < 1e-6) continue;
    const ux = dx / length, uz = dz / length;
    const across = (point: Vec2) => Math.abs((point.x - wall.start.x) * uz - (point.z - wall.start.z) * ux);
    if (across(a) > 1e-3 || across(b) > 1e-3) continue;
    const along = (middle.x - wall.start.x) * ux + (middle.z - wall.start.z) * uz;
    if (along < -1e-3 || along > length + 1e-3) continue;
    const distance = across(middle);
    if (!best || distance < best.distance) best = { wall, distance };
  }
  return best?.wall;
}

/** The wall along each edge of a room's outline, where there is one. */
export function edgeWalls(level: Pick<Level, 'walls'>, room: Pick<Room, 'polygon'>): (Wall | undefined)[] {
  const polygon = room.polygon;
  return polygon.map((point, index) => wallOnEdge(level.walls, point, polygon[(index + 1) % polygon.length]));
}

/**
 * The outline with each edge moved inward by its distance (outward when negative); a corner goes where its two moved
 * edges meet, and a corner between two edges in line stays on them.
 */
export function offsetPolygon(polygon: readonly Vec2[], distances: readonly number[]): Vec2[] {
  const count = polygon.length;
  if (count < 3) return polygon.map(point => ({ ...point }));
  const orientation = Math.sign(polygonArea(polygon as Vec2[])) || 1;
  const lines = polygon.map((a, index) => {
    const b = polygon[(index + 1) % count], dx = b.x - a.x, dz = b.z - a.z, length = Math.hypot(dx, dz) || 1;
    const inward = { x: -dz / length * orientation, z: dx / length * orientation }, distance = distances[index] ?? 0;
    return { point: { x: a.x + inward.x * distance, z: a.z + inward.z * distance }, direction: { x: dx / length, z: dz / length } };
  });
  return polygon.map((_, index) => {
    const before = lines[(index - 1 + count) % count], after = lines[index];
    const cross = before.direction.x * after.direction.z - before.direction.z * after.direction.x;
    if (Math.abs(cross) < 1e-9) return { ...after.point };
    const t = ((after.point.x - before.point.x) * after.direction.z - (after.point.z - before.point.z) * after.direction.x) / cross;
    return { x: before.point.x + before.direction.x * t, z: before.point.z + before.direction.z * t };
  });
}

/** Half of each edge's wall: how far the floor stands in from the axis. A room of no walls at all (a bare outline) is
 * measured as if its walls were the default ones, as the app does. */
export function floorInsets(level: Pick<Level, 'walls'>, room: Pick<Room, 'polygon'>): number[] {
  const walls = edgeWalls(level, room), walled = walls.some(Boolean);
  return walls.map(wall => (wall?.thickness ?? (walled ? 0 : DEFAULT_WALL_THICKNESS)) / 2);
}

/** The floor outline: each edge moved inward by half the thickness of the wall along it. */
export function roomFloorPolygon(level: Pick<Level, 'walls'>, room: Pick<Room, 'polygon'>): Vec2[] {
  return offsetPolygon(room.polygon, floorInsets(level, room));
}

/** Areas already measured, by the walls and the outline they were measured from: the plan asks on every frame. */
const measured = new WeakMap<readonly Wall[], WeakMap<readonly Vec2[], number>>();

/** The floor's area, m², as the app shows it. */
export function floorArea(level: Pick<Level, 'walls'>, room: Pick<Room, 'polygon'>): number {
  let byOutline = measured.get(level.walls);
  if (!byOutline) { byOutline = new WeakMap(); measured.set(level.walls, byOutline); }
  let area = byOutline.get(room.polygon);
  if (area === undefined) { area = Math.abs(polygonArea(roomFloorPolygon(level, room))); byOutline.set(room.polygon, area); }
  return area;
}

/** The floor's extent along the plan's axes: its width (x) and length (z), as the app's room size reads them. */
export function floorExtent(level: Pick<Level, 'walls'>, room: Pick<Room, 'polygon'>): { width: number; depth: number; minX: number; maxX: number; minZ: number; maxZ: number } {
  const floor = roomFloorPolygon(level, room);
  const xs = floor.map(point => point.x), zs = floor.map(point => point.z);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minZ = Math.min(...zs), maxZ = Math.max(...zs);
  return { width: maxX - minX, depth: maxZ - minZ, minX, maxX, minZ, maxZ };
}

/** Every room's floor on every level, m²: what a project card says. */
export function projectFloorArea(levels: readonly Pick<Level, 'walls' | 'rooms'>[]): number {
  return levels.reduce((sum, level) => sum + level.rooms.reduce((area, room) => area + floorArea(level, room), 0), 0);
}

/**
 * Whether a room lies beside a wall on its front (the side its normal points to) and on its back, probed just past
 * each face at its middle. A wall with a room on one side only is an outside wall of the floor (the app's
 * `WallController.IsExteriorWall`).
 */
export function roomsBesideWall(level: Pick<Level, 'rooms'>, wall: Pick<Wall, 'center' | 'normalIn' | 'thickness'>): { front: boolean; back: boolean } {
  const side = (sign: number) => {
    const reach = sign * (wall.thickness / 2 + .05), probe = { x: wall.center.x + wall.normalIn.x * reach, z: wall.center.z + wall.normalIn.z * reach };
    return level.rooms.some(room => room.polygon.length >= 3 && contains(room.polygon, probe));
  };
  return { front: side(1), back: side(-1) };
}
